import React, { useState, useEffect, useRef } from 'react';
import { useCategory } from '../context/CategoryContext';
import './Sidebar.css';

const Sidebar = ({ onFilterChange }) => {
  const { categories, loading, error, subcategories, fetchSubcategories } = useCategory();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const loadedCategoriesRef = useRef(new Set());

  // Fetch subcategories for each category on mount
  useEffect(() => {
    const loadAllSubcategories = async () => {
      for (const category of categories) {
        // Skip if already loaded or currently loading
        if (loadedCategoriesRef.current.has(category.id)) {
          continue;
        }
        
        // Skip if already in context cache
        if (subcategories[category.id]) {
          loadedCategoriesRef.current.add(category.id);
          continue;
        }
        
        try {
          loadedCategoriesRef.current.add(category.id);
          await fetchSubcategories(category.id);
        } catch (err) {
          console.error(`Failed to load subcategories for category ${category.id}:`, err);
        }
      }
    };

    if (categories.length > 0) {
      loadAllSubcategories();
    }
  }, [categories, fetchSubcategories, subcategories]);

  const handleCategoryChange = (categoryId, categoryName) => {
    // Only allow one category at a time - deselect previous and select new one
    const updatedCategories = selectedCategories.includes(categoryId)
      ? [] // Deselect if already selected
      : [categoryId]; // Select only this category (deselects others)
    
    setSelectedCategories(updatedCategories);
    
    // Immediately apply filters when category is selected
    if (onFilterChange) {
      onFilterChange({
        categories: updatedCategories,
        subcategories: [] // Clear subcategories when category changes
      });
    }
  };

  const handleSubcategoryChange = (subcategoryId, subcategoryName) => {
    const updatedSubcategories = selectedSubcategories.includes(subcategoryId)
      ? selectedSubcategories.filter(id => id !== subcategoryId)
      : [...selectedSubcategories, subcategoryId];
    
    setSelectedSubcategories(updatedSubcategories);
    
    // Immediately apply filters when subcategory is selected
    if (onFilterChange) {
      onFilterChange({
        categories: selectedCategories,
        subcategories: updatedSubcategories
      });
    }
  };

  const handleApplyFilters = () => {
    if (onFilterChange) {
      onFilterChange({
        categories: selectedCategories,
        subcategories: selectedSubcategories
      });
    }
    setIsSidebarOpen(false);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <>
      <button className="filter-toggle-btn" onClick={handleToggleSidebar}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="4" y1="6" x2="20" y2="6"></line>
          <line x1="4" y1="12" x2="14" y2="12"></line>
          <line x1="4" y1="18" x2="8" y2="18"></line>
        </svg>
        Filters
      </button>

      <div className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <h3>Categories</h3>
          <button className="close-btn" onClick={handleCloseSidebar}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div className="sidebar-content">
          {loading && categories.length === 0 ? (
            <div className="sidebar-loading">Loading categories...</div>
          ) : error && categories.length === 0 ? (
            <div className="sidebar-error">Error loading categories</div>
          ) : categories.length === 0 ? (
            <div className="sidebar-empty">No categories available</div>
          ) : (
            categories.map((category) => (
              <div key={category.id} className="category-group">
                <div className="category-item">
                  <input
                    type="checkbox"
                    id={`category-${category.id}`}
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => handleCategoryChange(category.id, category.name)}
                    className="category-checkbox"
                  />
                  <label htmlFor={`category-${category.id}`} className="category-label">
                    {category.name}
                  </label>
                </div>
                
                <div className="subcategories">
                  {(subcategories[category.id] || []).map((subcategory) => {
                    const subcategoryId = `${category.id}-${subcategory.id}`;
                    return (
                      <div key={subcategory.id} className="subcategory-item">
                        <input
                          type="checkbox"
                          id={`subcategory-${subcategoryId}`}
                          checked={selectedSubcategories.includes(subcategoryId)}
                          onChange={() => handleSubcategoryChange(subcategoryId, subcategory.name)}
                          className="subcategory-checkbox"
                        />
                        <label htmlFor={`subcategory-${subcategoryId}`} className="subcategory-label">
                          {subcategory.name}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="sidebar-footer">
          <button className="apply-btn" onClick={handleApplyFilters}>
            Apply Filters
          </button>
        </div>
      </div>

      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={handleCloseSidebar}></div>
      )}
    </>
  );
};

export default Sidebar;
